import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  Heart, Pill, AlertTriangle, Syringe, Activity, FileText,
  User, BarChart3, Building2, Paperclip, Code, Save, Check, Loader2,
  ChevronDown,
} from 'lucide-react';

// ── Props & shared types ──────────────────────────────────────────────────────

interface FHIRPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  bundle: Record<string, unknown> | null;
}

interface FHIRResource {
  resourceType: string;
  id?: string;
  [key: string]: unknown;
}

// ── Utility helpers ───────────────────────────────────────────────────────────

function getEntries(bundle: Record<string, unknown>, resourceType: string): FHIRResource[] {
  const entries = (bundle.entry as Array<{ resource?: FHIRResource }>) || [];
  return entries
    .map(e => e.resource)
    .filter((r): r is FHIRResource => !!r && r.resourceType === resourceType);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function coding(obj?: unknown): string {
  if (!obj) return '—';
  const c = obj as { coding?: Array<{ display?: string; code?: string }>; text?: string };
  return c.text || c.coding?.[0]?.display || c.coding?.[0]?.code || '—';
}

function extractPatientName(bundle: Record<string, unknown>): string {
  const patients = getEntries(bundle, 'Patient');
  if (patients.length === 0) return 'Unknown Patient';
  const p = patients[0] as { name?: Array<{ given?: string[]; family?: string }> };
  const name = p.name?.[0];
  return [name?.given?.join(' '), name?.family].filter(Boolean).join(' ') || 'Unknown Patient';
}

// ── IndexedDB helpers ─────────────────────────────────────────────────────────

const DB_NAME = 'wallet';
const DB_VERSION = 1;
const STORE_NAME = 'people';

function openWalletDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME); // out-of-line key — caller supplies key
      }
    };

    request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    request.onerror = () => reject(request.error);
  });
}

async function savePersonToWallet(
  patientName: string,
  bundle: Record<string, unknown>,
): Promise<void> {
  const db = await openWalletDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    // Value: wrap bundle with metadata; key: patient name
    const record = { bundle, savedAt: new Date().toISOString() };
    store.put(record, patientName);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror   = () => { db.close(); reject(tx.error); };
    tx.onabort   = () => { db.close(); reject(new Error('IndexedDB transaction aborted')); };
  });
}

// ── Section renderers ─────────────────────────────────────────────────────────

function PatientSection({ bundle }: { bundle: Record<string, unknown> }) {
  const patients = getEntries(bundle, 'Patient');
  if (patients.length === 0) return null;
  const p = patients[0] as {
    name?: Array<{ given?: string[]; family?: string }>;
    birthDate?: string;
    gender?: string;
  };
  const name = p.name?.[0];
  const fullName = [name?.given?.join(' '), name?.family].filter(Boolean).join(' ') || '—';

  return (
    <Card className="p-4 mb-3 flex items-center gap-4">
      <User className="h-5 w-5 text-blue-500 shrink-0" />
      <div className="flex gap-6 flex-wrap text-sm">
        <span><span className="text-muted-foreground">Name: </span><strong>{fullName}</strong></span>
        {p.birthDate && <span><span className="text-muted-foreground">DOB: </span>{formatDate(p.birthDate)}</span>}
        {p.gender   && <span><span className="text-muted-foreground">Gender: </span>{p.gender}</span>}
      </div>
    </Card>
  );
}

function ConditionsSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'Condition');
  if (items.length === 0) return <EmptyState message="No conditions recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const c = r as { code?: unknown; clinicalStatus?: unknown; onsetDateTime?: string; recordedDate?: string };
        const status = coding(c.clinicalStatus);
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(c.code)}</span>
              </div>
              <Badge variant={status === 'active' ? 'destructive' : 'secondary'} className="text-xs shrink-0">
                {status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Onset: {formatDate(c.onsetDateTime || c.recordedDate)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function MedicationsSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'MedicationRequest');
  if (items.length === 0) return <EmptyState message="No medications recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const m = r as {
          medicationCodeableConcept?: unknown;
          medicationReference?: { display?: string };
          status?: string;
          authoredOn?: string;
        };
        const name =
          coding(m.medicationCodeableConcept) !== '—'
            ? coding(m.medicationCodeableConcept)
            : m.medicationReference?.display || '—';
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Pill className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{name}</span>
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{m.status || '—'}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Prescribed: {formatDate(m.authoredOn)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function AllergiesSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'AllergyIntolerance');
  if (items.length === 0) return <EmptyState message="No allergies recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const a = r as {
          code?: unknown;
          clinicalStatus?: unknown;
          criticality?: string;
          recordedDate?: string;
          reaction?: Array<{ manifestation?: unknown[] }>;
        };
        const reaction = (a.reaction?.[0]?.manifestation as Array<unknown>)?.[0];
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(a.code)}</span>
              </div>
              {a.criticality && (
                <Badge
                  variant={a.criticality === 'high' ? 'destructive' : 'outline'}
                  className="text-xs shrink-0"
                >
                  {a.criticality}
                </Badge>
              )}
            </div>
            {reaction && (
              <p className="text-xs text-muted-foreground mt-1 ml-6">Reaction: {coding(reaction)}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5 ml-6">
              Recorded: {formatDate(a.recordedDate)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function ImmunizationsSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'Immunization');
  if (items.length === 0) return <EmptyState message="No immunizations recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        // occurrenceDateTime = R4; date = STU3 fallback
        const imm = r as { vaccineCode?: unknown; status?: string; occurrenceDateTime?: string; date?: string };
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Syringe className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(imm.vaccineCode)}</span>
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{imm.status || '—'}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Date: {formatDate(imm.occurrenceDateTime || imm.date)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function ObservationsSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'Observation');
  if (items.length === 0) return <EmptyState message="No observations recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const obs = r as {
          code?: unknown;
          valueQuantity?: { value?: number; unit?: string };
          valueString?: string;
          valueCodeableConcept?: unknown;
          effectiveDateTime?: string;
          status?: string;
        };
        const value = obs.valueQuantity
          ? `${obs.valueQuantity.value ?? ''} ${obs.valueQuantity.unit ?? ''}`.trim()
          : obs.valueString || coding(obs.valueCodeableConcept);
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(obs.code)}</span>
              </div>
              {value && value !== '—' && (
                <span className="text-sm font-semibold text-right shrink-0">{value}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Date: {formatDate(obs.effectiveDateTime)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function ProceduresSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'Procedure');
  if (items.length === 0) return <EmptyState message="No procedures recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const proc = r as {
          code?: unknown;
          status?: string;
          performedDateTime?: string;
          performedPeriod?: { start?: string };
        };
        const date = proc.performedDateTime || proc.performedPeriod?.start;
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(proc.code)}</span>
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{proc.status || '—'}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Date: {formatDate(date)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

// ── New sections (R4 additions) ───────────────────────────────────────────────

function DiagnosticReportSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'DiagnosticReport');
  if (items.length === 0) return <EmptyState message="No lab or diagnostic reports recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const report = r as {
          code?: unknown;
          status?: string;
          effectiveDateTime?: string;
          effectivePeriod?: { start?: string };
          category?: unknown[];
          conclusion?: string;
        };
        const date =
          report.effectiveDateTime ||
          (report.effectivePeriod as { start?: string } | undefined)?.start;
        const category = report.category?.[0] ? coding(report.category[0]) : null;
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{coding(report.code)}</span>
              </div>
              <div className="flex gap-1 shrink-0">
                {category && category !== '—' && (
                  <Badge variant="secondary" className="text-xs">{category}</Badge>
                )}
                <Badge variant="outline" className="text-xs">{report.status || '—'}</Badge>
              </div>
            </div>
            {report.conclusion && (
              <p className="text-xs text-muted-foreground mt-1 ml-6">{report.conclusion}</p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5 ml-6">Date: {formatDate(date)}</p>
          </Card>
        );
      })}
    </div>
  );
}

function EncounterSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'Encounter');
  if (items.length === 0) return <EmptyState message="No encounters recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const enc = r as {
          type?: unknown[];
          class?: { code?: string; display?: string };
          status?: string;
          period?: { start?: string; end?: string };
          reasonCode?: unknown[];
        };
        const typeLabel = enc.type?.[0] ? coding(enc.type[0]) : 'Encounter';
        const classLabel = enc.class?.display || enc.class?.code;
        const reason = enc.reasonCode?.[0] ? coding(enc.reasonCode[0]) : null;
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{typeLabel}</span>
              </div>
              <div className="flex gap-1 shrink-0">
                {classLabel && (
                  <Badge variant="secondary" className="text-xs">{classLabel}</Badge>
                )}
                <Badge variant="outline" className="text-xs">{enc.status || '—'}</Badge>
              </div>
            </div>
            {reason && reason !== '—' && (
              <p className="text-xs text-muted-foreground mt-1 ml-6">Reason: {reason}</p>
            )}
            {enc.period?.start && (
              <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                {formatDate(enc.period.start)}
                {enc.period.end ? ` – ${formatDate(enc.period.end)}` : ''}
              </p>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function DocumentReferenceSection({ bundle }: { bundle: Record<string, unknown> }) {
  const items = getEntries(bundle, 'DocumentReference');
  if (items.length === 0) return <EmptyState message="No documents recorded" />;
  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const doc = r as {
          type?: unknown;
          description?: string;
          status?: string;
          date?: string;
          content?: Array<{ attachment?: { title?: string; contentType?: string } }>;
        };
        const title = doc.description || coding(doc.type);
        const attachment = doc.content?.[0]?.attachment;
        return (
          <Card key={r.id || i} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{title}</span>
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{doc.status || '—'}</Badge>
            </div>
            {attachment?.title && (
              <p className="text-xs text-muted-foreground mt-1 ml-6">File: {attachment.title}</p>
            )}
            {attachment?.contentType && (
              <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                Type: {attachment.contentType}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5 ml-6">
              Date: {formatDate(doc.date)}
            </p>
          </Card>
        );
      })}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground text-center py-6">{message}</p>;
}

// ── Collapsible section ───────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon: Icon, iconColor, count, children }: CollapsibleSectionProps) {
  // Open by default when there is data; collapsed when empty
  const [open, setOpen] = useState(count > 0);

  return (
    <div className="border rounded-lg overflow-hidden mb-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/40 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
          <span>{title}</span>
          {count > 0 && (
            <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 font-medium">
              {count}
            </span>
          )}
          {count === 0 && (
            <span className="text-xs text-muted-foreground">none</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="px-4 pt-3 pb-4 border-t space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Section registry ──────────────────────────────────────────────────────────

const SECTIONS = [
  { title: 'Conditions',    icon: Heart,         iconColor: 'text-red-400',    resourceType: 'Condition',          Section: ConditionsSection },
  { title: 'Medications',   icon: Pill,          iconColor: 'text-blue-400',   resourceType: 'MedicationRequest',  Section: MedicationsSection },
  { title: 'Allergies',     icon: AlertTriangle, iconColor: 'text-orange-400', resourceType: 'AllergyIntolerance', Section: AllergiesSection },
  { title: 'Vaccines',      icon: Syringe,       iconColor: 'text-green-500',  resourceType: 'Immunization',       Section: ImmunizationsSection },
  { title: 'Vitals & Labs', icon: Activity,      iconColor: 'text-purple-400', resourceType: 'Observation',        Section: ObservationsSection },
  { title: 'Procedures',    icon: FileText,      iconColor: 'text-gray-400',   resourceType: 'Procedure',          Section: ProceduresSection },
  { title: 'Lab Reports',   icon: BarChart3,     iconColor: 'text-teal-500',   resourceType: 'DiagnosticReport',   Section: DiagnosticReportSection },
  { title: 'Encounters',    icon: Building2,     iconColor: 'text-indigo-500', resourceType: 'Encounter',          Section: EncounterSection },
  { title: 'Documents',     icon: Paperclip,     iconColor: 'text-slate-500',  resourceType: 'DocumentReference',  Section: DocumentReferenceSection },
];

// ── Main component ────────────────────────────────────────────────────────────

export function FHIRPreviewModal({ open, onOpenChange, fileName, bundle }: FHIRPreviewModalProps) {
  const { toast } = useToast();
  const [showRaw, setShowRaw] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Reset view state whenever a new bundle is loaded
  useEffect(() => {
    setShowRaw(false);
    setSaveState('idle');
  }, [bundle]);

  if (!bundle) return null;

  const totalResources = (bundle.entry as unknown[] | undefined)?.length ?? 0;
  const patientName = extractPatientName(bundle);

  const handleSave = async () => {
    if (saveState !== 'idle') return;
    setSaveState('saving');
    try {
      await savePersonToWallet(patientName, bundle);
      setSaveState('saved');
      toast({
        title: 'Saved to Wallet',
        description: `${patientName}'s health data saved locally.`,
      });
      setTimeout(() => setSaveState('idle'), 2500);
    } catch (err) {
      console.error('[WalletDB] Save failed:', err);
      setSaveState('idle');
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Could not save to local wallet.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0">

        {/* ── Fixed header — title only; X button has clear space on the right ── */}
        <DialogHeader className="px-6 pt-5 pb-3 shrink-0">
          <DialogTitle className="text-base font-semibold truncate pr-8">{fileName}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            FHIR Bundle · {totalResources} {totalResources === 1 ? 'resource' : 'resources'}
            {patientName !== 'Unknown Patient' && (
              <span className="ml-2 text-foreground/60">· {patientName}</span>
            )}
          </p>
        </DialogHeader>

        {/* ── Fixed action toolbar ── */}
        <div className="px-6 pb-3 shrink-0 border-b flex flex-wrap items-center gap-2">
          <Button
            variant={showRaw ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowRaw(v => !v)}
            className="gap-1.5 h-8 text-xs"
          >
            <Code className="h-3.5 w-3.5" />
            {showRaw ? 'Formatted' : 'Raw JSON'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={saveState === 'saving'}
            className="gap-1.5 h-8 text-xs"
          >
            {saveState === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saveState === 'saved'   && <Check   className="h-3.5 w-3.5 text-green-500" />}
            {saveState === 'idle'    && <Save    className="h-3.5 w-3.5" />}
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved!' : 'Save'}
          </Button>
        </div>

        {/*
         * ── Single scrolling body ──────────────────────────────────────────
         * One overflow-y-auto region for everything below the fixed header.
         * No nested scroll containers → no nested scrollbars.
         * Native overflow-y-auto (not ScrollArea) is required so that
         * position:sticky on the tab bar works correctly.
         */}
        <div className="flex-1 min-h-0 overflow-y-auto">

          {showRaw ? (
            /* Raw JSON — scrolls naturally within the body */
            <div className="px-6 py-4">
              <pre className="text-xs font-mono bg-muted rounded-md p-4 whitespace-pre-wrap break-all leading-relaxed">
                {JSON.stringify(bundle, null, 2)}
              </pre>
            </div>
          ) : (
            /* Formatted accordion view — no tabs, single scroll */
            <>
              {/* Patient summary */}
              <div className="px-6 pt-4 pb-3">
                <PatientSection bundle={bundle} />
              </div>

              {/* Collapsible sections — one per resource type */}
              <div className="px-6 pb-8">
                {SECTIONS.map(s => {
                  const count = getEntries(bundle, s.resourceType).length;
                  return (
                    <CollapsibleSection
                      key={s.resourceType}
                      title={s.title}
                      icon={s.icon}
                      iconColor={s.iconColor}
                      count={count}
                    >
                      <s.Section bundle={bundle} />
                    </CollapsibleSection>
                  );
                })}
              </div>
            </>
          )}

        </div>

      </DialogContent>
    </Dialog>
  );
}
